import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form, notification } from 'antd'
import axios from 'axios'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  hasLinkErrorState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import { AnnotationType, BuildingFile } from 'types/building'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'
import Krpano from 'utils/krpano'

import useDirectory from './useDirectory'
import useMutation from './useMutation'

export default function useAnnotation() {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const [form] = Form.useForm()
  const { navigate, getBuiildingFolderList } = useDirectory()
  const [memoHtml, setMemoHtml] = useState('')

  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const hasLinkError = useRecoilValue(hasLinkErrorState)

  const [isLoading, setIsLoading] = useState(false)

  const [selectedFolder, setSelectedFolder] = useState<AnnotationType>(
    currentSelected?.type || AnnotationType.ARCHITECTURE
  )
  const [selectedChildrenFolder, setSelectedChildrenFolder] = useState<{
    id: AnnotationType
    label: string
  }>()
  const isDataExistsAnnotation = Krpano.IsAnnotationDataEmpty(currentSelected)

  const { trigger, isMutating } = useMutation(`v1/annotations`)
  const { trigger: getPresignedUrl, isMutating: isGettingPresignedUrl } =
    useMutation('v1/annotations/presigned-urls')
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')

  const buildingFolder = useMemo(
    () => getBuiildingFolderList(),
    [getBuiildingFolderList]
  )

  useEffect(() => {
    const fetchContent = async () => {
      if (currentSelected?.memo) {
        const textContent = await Krpano.FetchMemoContent(currentSelected.memo)
        setMemoHtml(textContent)
      } else {
        setMemoHtml('')
      }
    }
    fetchContent()
  }, [currentSelected?.memo])

  const dataSource = useMemo(
    () => buildingFolder.find((folder) => folder.id === selectedFolder),
    [buildingFolder, selectedFolder]
  )
  const childrenFolder = useMemo(() => {
    const children = (dataSource?.children || []).filter(
      (item) => item.label !== '竣工図'
    )
    return [
      ...children,
      { label: 'リンク', id: 40, isFileHolder: true, children: null }
    ]
  }, [dataSource?.children])

  const hotspotLoadStyle = (krpano: any, data: any) => {
    krpano.call(`remove_annotation(${data.id})`)
    Krpano.AddAnnotation(krpano, [data])
  }

  const createNewAnnotation = (krpano: any, data: any) => {
    krpano.call('hide_temp_hotspot()')
    Krpano.AddAnnotation(krpano, [data])
  }

  const handleBack = () => {
    if (hasLinkError && selectedChildrenFolder?.id === 40) return
    selectedChildrenFolder && setSelectedChildrenFolder(undefined)
    if (
      selectedChildrenFolder?.id === 40 &&
      !currentSelected?.link &&
      currentSelected?.name
    ) {
      form.setFieldValue('name', '')
      setCurrentSelected((prev) => ({ ...prev, name: '' }))
    }
  }

  const handleCloseModal = () => {
    notification.destroy('placingAnnotation')
    if (selectedChildrenFolder) {
      setSelectedChildrenFolder(undefined)
    }
    if (!currentSelected?.id) {
      Krpano.RemoveTempAnnotation()
    }
    setCurrentSelected(undefined)
    setMemoHtml('')
  }

  const getImageID = () => {
    const isViewingSubImage = pointDetail?.subImages?.find(
      (item) => item.id === selectedSubImage
    )
    if (isViewingSubImage) {
      return selectedSubImage
    }
    return pointDetail?.image360?.id
  }

  const handleAddEditAnnotation = async (
    memo?: string,
    memo_content?: string,
    memo_file_name?: string
  ) => {
    const krpano = document.getElementById('embedpano-full') as any
    try {
      let memoKey = ''
      if (
        selectedFolder === AnnotationType.IMAGE ||
        selectedFolder === AnnotationType.INFO
      ) {
        const rawKey =
          currentSelected?.memo_content || currentSelected?.memo || ''
        memoKey = Krpano.GetMemoKeyFromPresignedUrl(rawKey)
        const isValidS3Key =
          memoKey &&
          typeof memoKey === 'string' &&
          !memoKey.startsWith('<') &&
          memoKey.length > 4 &&
          (memoKey.startsWith('annotation-content') ||
            memoKey.includes('/annotation-content'))
        if (isValidS3Key) {
          await deleteOldContent({
            data: {
              key: memoKey
            }
          })
        }
      }
      const convertedValue = {
        ...currentSelected,
        link:
          selectedFolder === AnnotationType.MOVIE
            ? (currentSelected?.memo_content || '').trim()
            : (currentSelected?.link || '').trim(),
        name: (currentSelected?.name || '').trim(),
        memo: memo || '',
        memo_content: memo_content || '',
        memo_file_name: memo_file_name || '',
        type: selectedFolder,
        files:
          currentSelected?.files &&
          currentSelected?.files.map((item) => item.id),
        imageID: getImageID()
      }
      const { data } = await trigger({
        method: 'put',
        data: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_id: currentSelectedPoint,
          annotation: convertedValue
        }
      })
      if (!currentSelected?.id) {
        setPointDetail(
          (item) =>
            item && {
              ...item,
              annotations: [...(item?.annotations || []), data]
            }
        )
        createNewAnnotation(krpano, data)
      } else {
        setPointDetail(
          (item) =>
            item && {
              ...item,
              annotations: item.annotations.map((anno) =>
                anno.id === currentSelected.id ? data : anno
              )
            }
        )
        hotspotLoadStyle(krpano, data)
      }
      notification.success({
        message: t('building.saved')
      })
      setIsLoading(false)
      handleCloseModal()
    } catch (error) {
      krpano.call('hide_temp_hotspot()')
      notification.destroy('placingAnnotation')
      setIsLoading(false)
      Utils.handleErrorNavigate(error, navigate)
    }
  }

  const validateAnnotationLink = () => {
    if (selectedChildrenFolder?.id === AnnotationType.MEMO) {
      const link = currentSelected?.link
      const name = currentSelected?.name

      const pattern: RegExp =
        // eslint-disable-next-line no-useless-escape
        /^(http(s)?:\/\/)?(?:www\.)?[-a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]{1,256}\.[a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]{1,6}\b(?:[-a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]*)$/
      const regexNonSpecialCha: RegExp =
        // eslint-disable-next-line no-irregular-whitespace
        /^[\p{L}\d０-９ 　「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=]+$/u

      const isLinkInvalid =
        link && (!pattern.test(link.trim()) || link.trim().length > 500)

      const isNameInvalid =
        name &&
        (!regexNonSpecialCha.test(name.trim()) || name.trim().length > 500)

      if (isLinkInvalid || isNameInvalid) {
        return
      }
    }
    handleAddEditAnnotation()
  }

  const handleSubmit = async (
    selectedImageFile?: File | null,
    selectedPdfFile?: File | null
  ) => {
    if (selectedChildrenFolder?.id === 40) {
      try {
        await form.validateFields()
      } catch (err) {
        return
      }
    }

    if (isMutating || isGettingPresignedUrl || isDeletingOldContent) return
    setIsLoading(true)
    try {
      let memoContent: string = ''
      let timestamp: number | undefined
      let extension: string | undefined
      let cleanFileName: string | undefined

      if (selectedFolder === AnnotationType.IMAGE) {
        if (selectedImageFile) {
          const fileExtension = selectedImageFile.name
            .toLowerCase()
            .split('.')
            .pop()
          const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'gif']
          if (!allowedImageExtensions.includes(fileExtension || '')) {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationImageTypeRequired')
            })
            return
          }

          const fileSizeInMB = selectedImageFile.size / (1024 * 1024)
          if (fileSizeInMB > 50) {
            setIsLoading(false)
            notification.error({
              message: t('common.validationError.fileTooLarge')
            })
            return
          }
        }
      } else if (selectedFolder === AnnotationType.MOVIE) {
        const youtubeUrl = currentSelected?.memo_content || ''
        if (youtubeUrl.trim()) {
          const youtubeRegex =
            /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/

          if (!youtubeRegex.test(youtubeUrl)) {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationYoutubeUrlRequired')
            })
            return
          }
          memoContent = youtubeUrl
        }
      } else if (selectedFolder === AnnotationType.INFO) {
        if (selectedPdfFile) {
          const fileExtension = selectedPdfFile.name
            .toLowerCase()
            .split('.')
            .pop()
          if (fileExtension !== 'pdf') {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationPdfTypeRequired')
            })
            return
          }

          const fileSizeInMB = selectedPdfFile.size / (1024 * 1024)
          if (fileSizeInMB > 50) {
            setIsLoading(false)
            notification.error({
              message: t('common.validationError.fileTooLarge')
            })
            return
          }
        }
      }

      let key: string | undefined

      const memoTextContent = memoHtml || ''

      if (memoTextContent && !Krpano.IsMemoEmptyCheckImage(memoTextContent)) {
        const newQuillInnerHtml = memoTextContent
        const file = new Blob([newQuillInnerHtml], {
          type: 'text/plain'
        })
        if (file.size / (1024 * 1024) > 50) {
          notification.error({
            key: 'memoTooLarge',
            message: t('common.validationError.fileTooLarge')
          })
          return
        }

        const data = await getPresignedUrl({
          data: {
            extension: 'txt',
            fileName: 'annotation-content'
          }
        })
        const { key: textKey = undefined, presignedUrl = undefined } = {
          ...data?.data?.s3PreSignedLinks[0]
        }
        await axios.put(presignedUrl, file)
        key = textKey
      }

      if (selectedFolder === AnnotationType.IMAGE && selectedImageFile) {
        timestamp = Date.now()
        extension = selectedImageFile.name.split('.').pop() || 'jpg'
        cleanFileName = selectedImageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')

        const presignedData = await getPresignedUrl({
          data: {
            extension,
            fileName: 'annotation-content'
          }
        })

        const { key: fileKey, presignedUrl } =
          presignedData?.data?.s3PreSignedLinks[0] || {}

        if (fileKey && presignedUrl) {
          await axios.put(presignedUrl, selectedImageFile)
          memoContent = fileKey
        }
      } else if (selectedFolder === AnnotationType.INFO && selectedPdfFile) {
        timestamp = Date.now()
        extension = selectedPdfFile.name.split('.').pop() || 'pdf'
        cleanFileName = selectedPdfFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')

        const presignedData = await getPresignedUrl({
          data: {
            extension,
            fileName: 'annotation-content'
          }
        })

        const { key: fileKey, presignedUrl } =
          presignedData?.data?.s3PreSignedLinks[0] || {}

        if (fileKey && presignedUrl) {
          await axios.put(presignedUrl, selectedPdfFile)
          memoContent = fileKey
        }
      }

      if (memoContent && timestamp && extension && cleanFileName) {
        const newFile: BuildingFile = {
          id: timestamp,
          s3Path: memoContent,
          mimeType:
            selectedImageFile?.type || selectedPdfFile?.type || 'image/jpeg',
          extension,
          createdAt: new Date().toISOString(),
          name: cleanFileName,
          thumbnail: memoContent,
          pointId: null,
          takenAt: null
        }

        setCurrentSelected((prev) => ({
          ...prev,
          files: prev?.files ? [...prev.files, newFile] : [newFile]
        }))
      }

      let finalMemoContent = memoContent
      if (!memoContent && selectedFolder !== currentSelected?.type) {
        finalMemoContent = ''
      }

      if (selectedFolder === AnnotationType.INFO) {
        const memoContentParam = CommonFunction.extractS3Key(
          finalMemoContent ?? ''
        )

        await handleAddEditAnnotation(
          undefined,
          memoContentParam,
          selectedPdfFile?.name || currentSelected?.memo_file_name || ''
        )
      } else if (selectedFolder === AnnotationType.IMAGE) {
        const memoContentParam = CommonFunction.extractS3Key(
          finalMemoContent ?? ''
        )

        await handleAddEditAnnotation(
          key,
          memoContentParam,
          selectedImageFile?.name || currentSelected?.memo_file_name || ''
        )
      } else {
        await handleAddEditAnnotation(key, memoContent || '', '')
      }
    } catch (error) {
      setIsLoading(false)
      Utils.handleErrorNavigate(error, navigate)
    }
  }

  return {
    form,
    isLoading,
    isDataExistsAnnotation,
    dataSource,
    childrenFolder,
    memoHtml,
    setMemoHtml,
    currentSelected,
    setCurrentSelected,
    selectedFolder,
    setSelectedFolder,
    selectedChildrenFolder,
    setSelectedChildrenFolder,
    handleBack,
    handleCloseModal,
    validateAnnotationLink,
    handleSubmit
  }
}
